import { Badge, Box, Button, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent, DrawerHeader, DrawerOverlay, Flex, Heading, HStack, Spacer, Text, useDisclosure, useMediaQuery, VStack } from "@chakra-ui/react";
import { FiInfo } from "react-icons/fi";
import { formatCelular } from "../../../utils/formatCelular";
import { formatTime } from "../../../utils/formatTime";
import { DadosComplementares } from "../types";
import CargaItemProduto from "../types/carga_item_produto";
import { formatNumber } from "../../../utils/formatNumber";

interface IProps {
  dados_complementares: DadosComplementares | any;
  pessoa: string;
  label?: string;
  produtos?: CargaItemProduto[];
  vendedor?: {
    vendedor_codigo: string;
    vendedor_nome: string;
    vendedor_celular: string;
  } | null;
}

const Header = ({ label }: { label: string }) => {
  return (
    <Heading size="sm" borderBottom="1px" mb={1}>{label}</Heading>
  )
}

const CardInfo = ({ label, info, show }: { label: string; info: any; show: boolean; }) => {
  return (
    <>{show && (
      <>
        <Header label={label} />
        {info}
      </>
    )}
    </>
  )
}

const CardInfoProdutos = ({ produtos }: { produtos?: CargaItemProduto[] }) => {
  if (!produtos) {
    return <></>;
  }
  if (produtos.length === 0) {
    return <></>;
  }

  return (
    <div>
      <Heading size="md" mt={4} mb={1} textAlign="center" color="blue.600">
        Lista de Produtos
      </Heading>

      <VStack spacing={4} align="stretch">
        {produtos.map((produto, index) => (
          <Box
            key={produto.produto_codigo}
            borderWidth={2}
            borderRadius="lg"
            p={3}
            shadow="md"
            bg="white"
          >
            <VStack align="stretch" spacing={2}>
              <Flex align="center" flexDir={'column'} justifyContent={'center'}>
                <Badge colorScheme="blue" variant="solid" fontSize="sm" px={3} py={1}>
                  Código {produto.produto_codigo}
                </Badge>
                <Spacer />
                <Text fontSize="md" fontWeight="bold" color="gray.700" textAlign={'center'}>
                  {produto.produto_descricao}
                </Text>
              </Flex>

              <Divider />

              <VStack spacing={2} align="stretch">
                {produto.quantidade_caixas > 0 && <HStack justify="space-between">
                  <Text color="gray.600" fontWeight="medium">
                    Caixas:
                  </Text>
                  <Badge colorScheme="blue" variant="subtle" fontSize="md" px={3}>
                    {formatNumber(produto.quantidade_caixas, 0)}
                  </Badge>
                </HStack>}

                {produto.quantidade_caixas > 0 && <Divider />}

                {produto.quantidade_pacotes > 0 && <HStack justify="space-between">
                  <Text color="gray.600" fontWeight="medium">
                    Pacotes:
                  </Text>
                  <Badge colorScheme="blue" variant="subtle" fontSize="md" px={3}>
                    {formatNumber(produto.quantidade_pacotes, 0)}
                  </Badge>
                </HStack>}

                {produto.quantidade_pacotes > 0 && <Divider />}

                <HStack justify="space-between">
                  <Text color="gray.600" fontWeight="medium">
                    Valor unitário:
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold" color="blue.600">
                    R$ {formatNumber(parseFloat(produto.valor_unitario), 2)}
                  </Text>
                </HStack>

                <Divider />

                <HStack justify="space-between">
                  <Text color="gray.700" fontWeight="bold" fontSize="lg">
                  Valor total:
                  </Text>
                  <Text fontSize="xl" fontWeight="bold" color="green.600">
                    R$ {formatNumber(parseFloat(produto.valor_total), 2)}
                  </Text>
                </HStack>
              </VStack>
            </VStack>
          </Box>
        ))}

        <Box
          bg="blue.50"
          borderWidth={2}
          borderColor="blue.200"
          borderRadius="lg"
          p={2}
        >
          <HStack justify="space-between">
            <Text color="blue.700" fontWeight="bold" fontSize="lg">
              Total Geral:
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="blue.600">
              R$ {formatNumber(produtos.reduce((total, produto) => total + parseFloat(produto.valor_total), 0), 2)}
            </Text>
          </HStack>
        </Box>
      </VStack>
    </div>
  )
}

const DadosComplementaresButton = ({ dados_complementares, pessoa, vendedor = null, produtos, label = 'DETALHES' }: IProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [eMobile] = useMediaQuery('(max-width: 768px)')

  const getDiasFechado = () => {
    let retorno = "";

    if (dados_complementares.complemento_nao_abre_segunda == '1') {
      retorno += "Segunda-feira";
    }
    if (dados_complementares.complemento_nao_abre_terca == '1') {
      if (retorno) {
        retorno += ", Terça-feira";
      } else {
        retorno = "Terça-feira";
      }
    }
    if (dados_complementares.complemento_nao_abre_quarta == '1') {
      if (retorno) {
        retorno += ", Quarta-feira";
      } else {
        retorno = "Quarta-feira";
      }
    }
    if (dados_complementares.complemento_nao_abre_quinta == '1') {
      if (retorno) {
        retorno += ", Quinta-feira";
      } else {
        retorno = "Quinta-feira";
      }
    }
    if (dados_complementares.complemento_nao_abre_sexta == '1') {
      if (retorno) {
        retorno += ", Sexta-feira";
      } else {
        retorno = "Sexta-feira";
      }
    }
    if (dados_complementares.complemento_nao_abre_sabado == '1') {
      if (retorno) {
        retorno += ", Sábado";
      } else {
        retorno = "Sábado";
      }
    }
    if (dados_complementares.complemento_nao_abre_domingo == '1') {
      if (retorno) {
        retorno += ", Domingo";
      } else {
        retorno = "Domingo";
      }
    }

    return retorno;
  }

  return (
    <>
      <Button size="sm" colorScheme="blue" onClick={onOpen}><FiInfo fontSize="1.3em" /> <Text ml={1}>{label}</Text></Button>
      <Drawer placement={eMobile ? 'bottom' : 'right'} onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth='1px'>
            Dados Complementares <br />
            <Text fontSize="0.8rem">{pessoa}</Text>
          </DrawerHeader>
          <DrawerBody pb={20}>
            <CardInfo
              label="Data/Hora Recebimento 1"
              info={`Das ${formatTime(dados_complementares.complemento_hora_recebimento_inicial_1)} até ${formatTime(dados_complementares.complemento_hora_recebimento_final_1)}`}
              show={!!dados_complementares.complemento_hora_recebimento_inicial_1}
            />
            <CardInfo
              label="Data/Hora Recebimento 2"
              info={`Das ${formatTime(dados_complementares.complemento_hora_recebimento_inicial_2)} até ${formatTime(dados_complementares.complemento_hora_recebimento_final_2)}`}
              show={!!dados_complementares.complemento_hora_recebimento_inicial_2}
            />
            <CardInfo
              label="Estabelecimento Fechado em"
              info={getDiasFechado()}
              show={!!getDiasFechado()}
            />
            <CardInfo
              label="Observações Para a Entrega"
              info={dados_complementares.complemento_observacao_entrega}
              show={!!dados_complementares.complemento_observacao_entrega}
            />
            <CardInfo
              label="Representante do Pedido"
              info={<>
                {vendedor?.vendedor_codigo} - {vendedor?.vendedor_nome} <br />
                Celular: {formatCelular(vendedor?.vendedor_celular as string)}
              </>}
              show={!!vendedor}
            />

            <CardInfoProdutos produtos={produtos} />
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>

  )
}

export default DadosComplementaresButton;